import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Button,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Loader2, RefreshCw, AlertTriangle, Plus, Copy } from 'lucide-react';
import {
  getAgencyInsurances,
  createAgencyInsurance,
  updateAgencyInsurance,
  deleteAgencyInsurance,
  duplicateClinicInsurances,
  refreshPayorEnrollments,
  type AgencyInsuranceResponse,
  type AgencyClinicResponse,
} from '../../services/clinicsApi';
import InsuranceModal from '../../components/admin/InsuranceModal';
import type {
  InsuranceForm,
  InsuranceModalTab,
} from '../../components/admin/InsuranceModal';
import { FeatureFlag } from '../../constants/featureFlags';
import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { InsuranceTable } from '../../components/admin/InsuranceTable';
import {
  isMedicarePayorType,
  formatDuplicateInsurancePreview,
  previewDuplicateInsurances,
} from '../../components/admin/insuranceUtils';
import { getAgencyClinicDisplayName } from '../../utils/agencyClinicDisplayName';
import { clinicParticipatesInKinnser } from '../../utils/clinicKeyUtils';
import { ClinicSelect } from '../../components/clinic/ClinicSelect';

const DEFAULT_INSURANCE_FORM: InsuranceForm = {
  agency_clinic_id: '',
  name: '',
  edi_payer_id: '',
  edi_clearinghouse: '',
  visit_authorization_required: false,
  noa_required: false,
  noa_omit_admitting_diagnosis: false,
  include_location_codes_on_noa: false,
  payor_type: null,
  billing_frequency: 'MONTHLY',
  pdgm_enabled: false,
  charge_type: 'VISIT',
  type_of_bill: null,
  do_not_multiply_charges_by_units: false,
  health_plan_id: '',
  provider_code: '',
  provider_number: '',
  address_one: '',
  address_two: '',
  city: '',
  state: '',
  zip_code: '',
};

interface InsuranceSectionProps {
  clinics: AgencyClinicResponse[];
  isSuperUser: boolean;
  isRogerEmrEnabled: boolean;
  /** Incremented by the parent to force a reload (e.g. after a clinic info sync). */
  refreshToken?: number;
}

const InsuranceSection: React.FC<InsuranceSectionProps> = ({
  clinics,
  isSuperUser,
  isRogerEmrEnabled,
  refreshToken = 0,
}) => {
  const { canEdit } = useAdminSectionAccess('insurance');
  const {
    user,
    showSuccess,
    showError,
    hasFeatureFlag,
    fetchAgencyInsurances,
  } = useStore(
    useShallow(state => ({
      user: state.user,
      showSuccess: state.showSuccess,
      showError: state.showError,
      hasFeatureFlag: state.hasFeatureFlag,
      fetchAgencyInsurances: state.fetchAgencyInsurances,
    }))
  );

  const [insurances, setInsurances] = useState<AgencyInsuranceResponse[]>([]);
  const [refreshingClinicId, setRefreshingClinicId] = useState<string | null>(
    null
  );
  const [isLoadingInsurances, setIsLoadingInsurances] = useState(false);
  const [insuranceSearch, setInsuranceSearch] = useState('');
  // Single-clinic view — no "All Clinics"; default to the first clinic once loaded.
  const [clinicFilter, setClinicFilter] = useState<string>('');

  // Insurance create/edit modal state
  const [isInsuranceModalOpen, setIsInsuranceModalOpen] = useState(false);
  const [insuranceModalMode, setInsuranceModalMode] = useState<
    'create' | 'edit'
  >('create');
  const [editingInsuranceId, setEditingInsuranceId] = useState<string | null>(
    null
  );
  // True when editing a WellSky-synced insurance (clinic_key set): modal is view-only.
  const [editingInsuranceSynced, setEditingInsuranceSynced] = useState(false);
  // Tab the insurance modal opens on: a table-row rates action deep-links
  // straight to the Billing rates tab.
  const [insuranceModalInitialTab, setInsuranceModalInitialTab] =
    useState<InsuranceModalTab>('details');
  const [insuranceForm, setInsuranceForm] = useState<InsuranceForm>(
    DEFAULT_INSURANCE_FORM
  );
  // This applies only to a single new-insurance session; edits retain their
  // persisted/synced flag behavior.
  const [hasManuallyChangedNoaOmit, setHasManuallyChangedNoaOmit] =
    useState(false);
  const [isSavingInsurance, setIsSavingInsurance] = useState(false);

  // Delete confirmation state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingInsuranceId, setDeletingInsuranceId] = useState<string | null>(
    null
  );
  const [isDeletingInsurance, setIsDeletingInsurance] = useState(false);

  // Duplicate-insurances modal state
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateSourceClinicId, setDuplicateSourceClinicId] = useState('');
  const [duplicateDestClinicId, setDuplicateDestClinicId] = useState('');
  const [isDuplicatingInsurances, setIsDuplicatingInsurances] = useState(false);

  const showBillingView = hasFeatureFlag(FeatureFlag.ADMIN_TAB_BILLING_VIEW);

  const clinicsById = useMemo(() => {
    const map = new Map<string, AgencyClinicResponse>();
    for (const clinic of clinics) {
      map.set(clinic.id, clinic);
    }
    return map;
  }, [clinics]);

  // Keep the insurance list on a real clinic; pick the first if unset/stale.
  useEffect(() => {
    setClinicFilter(prev =>
      prev && clinics.some(clinic => clinic.id === prev)
        ? prev
        : (clinics[0]?.id ?? '')
    );
  }, [clinics]);

  // Load insurances
  const loadInsurances = async () => {
    if (!user?.agency?.id) return;

    setIsLoadingInsurances(true);
    try {
      const data = await getAgencyInsurances(user.agency.id);
      setInsurances(data);
    } catch (error) {
      console.error('Failed to load insurances:', error);
      showError('Failed to load insurance data');
    } finally {
      setIsLoadingInsurances(false);
    }
  };

  // This page keeps insurances in local state, but the shared store caches
  // agencyInsurances for the whole session and never invalidates on its own.
  // Without refreshing it here, consumers like intake's Prior Auth screen keep
  // the stale visit_authorization_required until a hard page refresh.
  const refreshInsurancesAfterMutation = async () => {
    await Promise.all([loadInsurances(), fetchAgencyInsurances()]);
  };

  useEffect(() => {
    if (user?.agency?.id) {
      loadInsurances();
    }
  }, [user?.agency?.id, refreshToken]);

  const handleRefreshEnrollments = async (agencyClinicId: string) => {
    setRefreshingClinicId(agencyClinicId);
    try {
      const { updatedCount, insurances: updated } =
        await refreshPayorEnrollments(agencyClinicId);
      // Splice the clinic's refreshed rows back into the full list.
      setInsurances(prev => {
        const byId = new Map(updated.map(row => [row.id, row]));
        return prev.map(row => byId.get(row.id) ?? row);
      });
      if (updatedCount === 0) {
        showSuccess(
          'No enrollment updates',
          'Stedi returned no enrollment records for this clinic.'
        );
      } else {
        showSuccess(
          'Enrollment status updated',
          'Fetched the latest enrollment status from Stedi.'
        );
      }
    } catch (error) {
      console.error('Failed to refresh payor enrollments:', error);
      showError(
        'Refresh failed',
        'Unable to fetch enrollment status. Please try again.'
      );
    } finally {
      setRefreshingClinicId(null);
    }
  };

  // Manual insurance create: Roger-only clinics (not Kinnser-participating).
  // Kinnser-participating clinics manage payers via clinic-info sync (EMR-375/379).
  const rogerEmrClinics = clinics.filter(c => !clinicParticipatesInKinnser(c));
  const canAddInsurance = isRogerEmrEnabled && rogerEmrClinics.length > 0;
  // Copy insurance/physicians only into non-Kinnser clinics (Kinnser payers
  // stay sync-owned). Need ≥1 Roger destination and ≥1 other clinic as source.
  const canCopyInsurancesToRogerClinic =
    clinics.length >= 2 && rogerEmrClinics.length >= 1;

  // Insurance modal handlers
  const handleCreateInsurance = () => {
    setInsuranceModalInitialTab('details');
    setInsuranceModalMode('create');
    setEditingInsuranceId(null);
    setEditingInsuranceSynced(false);
    setInsuranceForm(DEFAULT_INSURANCE_FORM);
    setHasManuallyChangedNoaOmit(false);
    setIsInsuranceModalOpen(true);
  };

  const handleEditInsurance = (
    insurance: AgencyInsuranceResponse,
    initialTab: InsuranceModalTab = 'details'
  ) => {
    setInsuranceModalInitialTab(initialTab);
    setInsuranceModalMode('edit');
    setEditingInsuranceId(insurance.id);
    setEditingInsuranceSynced(!!insurance.clinic_key);
    setInsuranceForm({
      agency_clinic_id: '',
      name: insurance.name,
      edi_payer_id: insurance.edi_payer_id || '',
      edi_clearinghouse: insurance.edi_clearinghouse || '',
      visit_authorization_required:
        insurance.visit_authorization_required ?? false,
      noa_required: insurance.noa_required ?? false,
      noa_omit_admitting_diagnosis:
        insurance.noa_omit_admitting_diagnosis ?? false,
      include_location_codes_on_noa:
        insurance.include_location_codes_on_noa ?? false,
      payor_type: insurance.payor_type ?? null,
      billing_frequency: insurance.billing_frequency ?? 'MONTHLY',
      // PDGM follows the stored flag on any payer type; unset rows default to
      // On only for Medicare types.
      pdgm_enabled:
        insurance.pdgm_enabled ??
        isMedicarePayorType(insurance.payor_type ?? null),
      charge_type: insurance.charge_type ?? 'VISIT',
      type_of_bill: insurance.type_of_bill ?? null,
      do_not_multiply_charges_by_units:
        insurance.do_not_multiply_charges_by_units ?? false,
      health_plan_id: insurance.health_plan_id ?? '',
      provider_code: insurance.provider_code ?? '',
      provider_number: insurance.provider_number ?? '',
      address_one: insurance.address_one ?? '',
      address_two: insurance.address_two ?? '',
      city: insurance.city ?? '',
      state: insurance.state ?? '',
      zip_code: insurance.zip_code ?? '',
    });
    setIsInsuranceModalOpen(true);
  };

  const handleSaveInsurance = async () => {
    if (insuranceModalMode === 'create') {
      if (!insuranceForm.agency_clinic_id) {
        showError('Please select a clinic');
        return;
      }
      if (!insuranceForm.name.trim()) {
        showError('Insurance name is required');
        return;
      }
      if (!insuranceForm.payor_type) {
        showError('Please select a payer type');
        return;
      }
      // EMR-880: a new Roger-EMR insurance must carry an explicit FL 4 scheme.
      // Mirrors the modal's disabled Save so the rule still holds if the button
      // is bypassed. Gated on the billing section being visible — that is where
      // the field lives.
      if (isRogerEmrEnabled && showBillingView && !insuranceForm.type_of_bill) {
        showError('Please select a type of bill');
        return;
      }
      setIsSavingInsurance(true);
      try {
        await createAgencyInsurance({
          agency_clinic_id: insuranceForm.agency_clinic_id,
          name: insuranceForm.name.trim(),
          edi_payer_id: insuranceForm.edi_payer_id.trim() || undefined,
          edi_clearinghouse:
            insuranceForm.edi_clearinghouse.trim() || undefined,
          visit_authorization_required:
            insuranceForm.visit_authorization_required,
          noa_required: insuranceForm.noa_required,
          noa_omit_admitting_diagnosis:
            insuranceForm.noa_omit_admitting_diagnosis,
          include_location_codes_on_noa:
            insuranceForm.include_location_codes_on_noa,
          payor_type: insuranceForm.payor_type!,
          billing_frequency: insuranceForm.billing_frequency,
          pdgm_enabled: insuranceForm.pdgm_enabled,
          charge_type: insuranceForm.charge_type,
          type_of_bill: insuranceForm.type_of_bill,
          do_not_multiply_charges_by_units:
            insuranceForm.do_not_multiply_charges_by_units,
          health_plan_id: insuranceForm.health_plan_id.trim() || null,
          provider_code: insuranceForm.provider_code.trim() || null,
          provider_number: insuranceForm.provider_number.trim() || null,
          address_one: insuranceForm.address_one.trim() || null,
          address_two: insuranceForm.address_two.trim() || null,
          city: insuranceForm.city.trim() || null,
          state: insuranceForm.state.trim() || null,
          zip_code: insuranceForm.zip_code.trim() || null,
        });
        showSuccess('Insurance created successfully');
        setIsInsuranceModalOpen(false);
        await refreshInsurancesAfterMutation();
      } catch (error) {
        console.error('Failed to create insurance:', error);
        showError('Failed to create insurance. Please try again.');
      } finally {
        setIsSavingInsurance(false);
      }
    } else {
      if (!editingInsuranceId) return;
      // A synced save sends only the NOA flags, never the name, so the name
      // requirement must not gate it — a WellSky row with a blank scraped name
      // would otherwise be unable to save the flags at all.
      if (!editingInsuranceSynced && !insuranceForm.name.trim()) {
        showError('Insurance name is required');
        return;
      }
      setIsSavingInsurance(true);
      try {
        if (editingInsuranceSynced) {
          // WellSky-synced rows accept only the Roger-native NOA flags.
          // Send nothing else — every other field is owned by the clinic-info
          // sync, and echoing a name read when the modal opened would write a
          // stale one back if the sync renamed the row in the meantime.
          await updateAgencyInsurance(editingInsuranceId, {
            noa_omit_admitting_diagnosis:
              insuranceForm.noa_omit_admitting_diagnosis,
            include_location_codes_on_noa:
              insuranceForm.include_location_codes_on_noa,
          });
        } else {
          await updateAgencyInsurance(editingInsuranceId, {
            name: insuranceForm.name.trim(),
            edi_payer_id: insuranceForm.edi_payer_id.trim() || null,
            edi_clearinghouse: insuranceForm.edi_clearinghouse.trim() || null,
            visit_authorization_required:
              insuranceForm.visit_authorization_required,
            noa_required: insuranceForm.noa_required,
            noa_omit_admitting_diagnosis:
              insuranceForm.noa_omit_admitting_diagnosis,
            include_location_codes_on_noa:
              insuranceForm.include_location_codes_on_noa,
            payor_type: insuranceForm.payor_type,
            billing_frequency: insuranceForm.billing_frequency,
            pdgm_enabled: insuranceForm.pdgm_enabled,
            charge_type: insuranceForm.charge_type,
            type_of_bill: insuranceForm.type_of_bill,
            do_not_multiply_charges_by_units:
              insuranceForm.do_not_multiply_charges_by_units,
            health_plan_id: insuranceForm.health_plan_id.trim() || null,
            provider_code: insuranceForm.provider_code.trim() || null,
            provider_number: insuranceForm.provider_number.trim() || null,
            address_one: insuranceForm.address_one.trim() || null,
            address_two: insuranceForm.address_two.trim() || null,
            city: insuranceForm.city.trim() || null,
            state: insuranceForm.state.trim() || null,
            zip_code: insuranceForm.zip_code.trim() || null,
          });
        }
        showSuccess('Insurance updated successfully');
        setIsInsuranceModalOpen(false);
        await refreshInsurancesAfterMutation();
      } catch (error) {
        console.error('Failed to update insurance:', error);
        showError('Failed to update insurance. Please try again.');
      } finally {
        setIsSavingInsurance(false);
      }
    }
  };

  const handleInsuranceFormChange = (updates: Partial<InsuranceForm>) => {
    const manuallyChangedNoaOmit =
      insuranceModalMode === 'create' &&
      updates.noa_omit_admitting_diagnosis !== undefined;
    if (manuallyChangedNoaOmit) {
      setHasManuallyChangedNoaOmit(true);
    }

    setInsuranceForm(prev => ({
      ...prev,
      ...updates,
      // EMR-1402: default this on every payer-type change only until the
      // user explicitly chooses the switch value in the current create session.
      noa_omit_admitting_diagnosis:
        insuranceModalMode === 'create' &&
        !hasManuallyChangedNoaOmit &&
        updates.payor_type !== undefined
          ? updates.payor_type === 'MEDICARE_TRADITIONAL'
          : (updates.noa_omit_admitting_diagnosis ??
            prev.noa_omit_admitting_diagnosis),
    }));
  };

  const handleCancelInsuranceModal = () => {
    setIsInsuranceModalOpen(false);
    setEditingInsuranceId(null);
    setInsuranceForm(DEFAULT_INSURANCE_FORM);
    setHasManuallyChangedNoaOmit(false);
  };

  const handleDeleteInsurance = (insuranceId: string) => {
    setDeletingInsuranceId(insuranceId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteInsurance = async () => {
    if (!deletingInsuranceId) return;

    setIsDeletingInsurance(true);
    try {
      await deleteAgencyInsurance(deletingInsuranceId);
      showSuccess('Insurance deleted successfully');
      setIsDeleteDialogOpen(false);
      setDeletingInsuranceId(null);
      await refreshInsurancesAfterMutation();
    } catch (error) {
      console.error('Failed to delete insurance:', error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to delete insurance. Please try again.';
      showError(message);
    } finally {
      setIsDeletingInsurance(false);
    }
  };

  const duplicateInsurancePreview = useMemo(() => {
    if (!duplicateSourceClinicId || !duplicateDestClinicId) {
      return null;
    }
    if (duplicateSourceClinicId === duplicateDestClinicId) {
      return null;
    }
    return previewDuplicateInsurances(
      insurances,
      duplicateSourceClinicId,
      duplicateDestClinicId
    );
  }, [duplicateDestClinicId, duplicateSourceClinicId, insurances]);

  const duplicateInsurancePreviewText = useMemo(() => {
    if (!duplicateSourceClinicId || !duplicateDestClinicId) {
      return null;
    }
    if (duplicateSourceClinicId === duplicateDestClinicId) {
      return 'Source and destination clinics must be different.';
    }
    if (!duplicateInsurancePreview) {
      return null;
    }
    return formatDuplicateInsurancePreview(duplicateInsurancePreview);
  }, [
    duplicateDestClinicId,
    duplicateInsurancePreview,
    duplicateSourceClinicId,
  ]);

  const handleOpenDuplicateInsurances = () => {
    setDuplicateSourceClinicId('');
    setDuplicateDestClinicId('');
    setIsDuplicateModalOpen(true);
  };

  const handleConfirmDuplicateInsurances = async () => {
    if (!duplicateSourceClinicId || !duplicateDestClinicId) {
      showError('Please select both a source and destination clinic');
      return;
    }
    if (duplicateSourceClinicId === duplicateDestClinicId) {
      showError('Source and destination clinics must be different');
      return;
    }
    setIsDuplicatingInsurances(true);
    try {
      const result = await duplicateClinicInsurances(
        duplicateSourceClinicId,
        duplicateDestClinicId
      );
      const copiedRates = result.copied_visit_rate_count ?? 0;
      showSuccess(
        `Copied ${result.created_count} insurance${
          result.created_count === 1 ? '' : 's'
        }` +
          (result.skipped_count > 0
            ? ` (${result.skipped_count} skipped — matching payer ID and name already present)`
            : '') +
          (copiedRates > 0
            ? `, including ${copiedRates} visit rate${
                copiedRates === 1 ? '' : 's'
              }`
            : '')
      );
      setIsDuplicateModalOpen(false);
      await loadInsurances();
    } catch (error) {
      console.error('Failed to copy insurances:', error);
      showError(
        error instanceof Error
          ? error.message
          : 'Failed to copy insurances. Please try again.'
      );
    } finally {
      setIsDuplicatingInsurances(false);
    }
  };

  // Filtered insurances — clinic search uses the live clinic name via FK.
  const filteredInsurances = insurances.filter(insurance => {
    const clinic = insurance.agency_clinic_id
      ? clinicsById.get(insurance.agency_clinic_id)
      : undefined;
    const clinicLabel = clinic
      ? getAgencyClinicDisplayName(clinic, {
          totalClinicCount: clinics.length,
        })
      : insurance.clinic_name;

    const query = insuranceSearch.toLowerCase();
    const matchesSearch =
      insuranceSearch === '' ||
      insurance.name.toLowerCase().includes(query) ||
      insurance.edi_payer_id.toLowerCase().includes(query) ||
      (clinicLabel ?? '').toLowerCase().includes(query);

    const matchesClinic =
      !!clinicFilter && insurance.agency_clinic_id === clinicFilter;

    return matchesSearch && matchesClinic;
  });

  return (
    <>
      {/* Insurance Providers Section */}
      <div className='space-y-6'>
        <div className='space-y-1.5'>
          <div className='flex justify-between items-start'>
            <div>
              <p className='text-sm text-muted-foreground'>
                {isRogerEmrEnabled
                  ? 'Manage insurance providers configured for your agency'
                  : 'View insurance providers configured for your agency'}
              </p>
            </div>
            {isRogerEmrEnabled && canEdit && (
              <div className='flex gap-2'>
                {isSuperUser && canCopyInsurancesToRogerClinic && (
                  <Button
                    onClick={handleOpenDuplicateInsurances}
                    variant='outline'
                  >
                    <Copy className='mr-2 h-4 w-4' />
                    Copy to Clinic...
                  </Button>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          onClick={handleCreateInsurance}
                          variant='outline'
                          disabled={!canAddInsurance}
                        >
                          <Plus className='mr-2 h-4 w-4' />
                          Add Insurance
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!canAddInsurance && (
                      <TooltipContent>
                        <p>
                          You need to create a clinic first before adding
                          insurance
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </div>
        <div className='space-y-4'>
          <div className='flex flex-col lg:flex-row gap-4'>
            <div className='flex-1'>
              <Input
                placeholder='Search insurances...'
                value={insuranceSearch}
                onChange={e => setInsuranceSearch(e.target.value)}
                className='w-full'
              />
            </div>
            <div className='w-full lg:w-64'>
              <ClinicSelect
                clinics={clinics}
                value={clinicFilter}
                onChange={e => setClinicFilter(e.target.value)}
                placeholder='Select clinic'
              />
            </div>
            <Button
              onClick={loadInsurances}
              disabled={isLoadingInsurances}
              variant='outline'
            >
              {isLoadingInsurances ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <RefreshCw className='h-4 w-4' />
              )}
            </Button>
          </div>

          {isLoadingInsurances ? (
            <div className='rounded-md border py-8 text-center'>
              <Loader2 className='h-6 w-6 animate-spin mx-auto' />
              <p className='text-sm text-muted-foreground mt-2'>
                Loading insurances...
              </p>
            </div>
          ) : insurances.length === 0 ? (
            <div className='rounded-md border py-8 text-center text-sm text-muted-foreground'>
              {isRogerEmrEnabled
                ? 'No insurance providers found. Click "Add Insurance" to create one.'
                : 'No insurance providers found. Try updating from clinic info first.'}
            </div>
          ) : (
            <InsuranceTable
              insurances={filteredInsurances}
              clinics={clinics}
              selectedClinicId={clinicFilter || null}
              isRogerEmrEnabled={isRogerEmrEnabled}
              onEdit={handleEditInsurance}
              onDelete={handleDeleteInsurance}
              canEdit={canEdit}
              showWellSkyBadge={showBillingView}
              showEnrollmentColumns={showBillingView}
              onRefreshEnrollments={
                isRogerEmrEnabled ? handleRefreshEnrollments : undefined
              }
              refreshingClinicId={refreshingClinicId}
              onEditRates={
                showBillingView
                  ? insurance => handleEditInsurance(insurance, 'rates')
                  : undefined
              }
            />
          )}
        </div>
      </div>

      {/* Insurance Create/Edit Modal */}
      {isRogerEmrEnabled && (
        <InsuranceModal
          open={isInsuranceModalOpen}
          mode={insuranceModalMode}
          form={insuranceForm}
          onChange={handleInsuranceFormChange}
          onSave={handleSaveInsurance}
          onCancel={handleCancelInsuranceModal}
          clinics={rogerEmrClinics}
          saving={isSavingInsurance}
          wellSkySynced={editingInsuranceSynced}
          showBillingConfig={showBillingView}
          insuranceId={editingInsuranceId}
          initialTab={insuranceModalInitialTab}
          isRogerEmrEnabled={isRogerEmrEnabled}
        />
      )}

      {/* Duplicate Insurances Modal */}
      {isRogerEmrEnabled && isSuperUser && (
        <Dialog
          open={isDuplicateModalOpen}
          onOpenChange={setIsDuplicateModalOpen}
        >
          <DialogContent className='sm:max-w-[480px]'>
            <DialogHeader>
              <DialogTitle>Copy Insurances to Clinic</DialogTitle>
              <DialogDescription>
                Copy every insurance from one clinic onto a Roger-only clinic
                (not Kinnser-linked).
              </DialogDescription>
            </DialogHeader>
            <div className='flex items-start gap-2 rounded-md border border-border bg-muted/50 p-3 text-xs text-muted-foreground'>
              <AlertTriangle className='h-4 w-4 shrink-0 mt-0.5' />
              <span>
                Creates Roger-managed insurance copies, not WellSky-synced rows.
                Also copies visit rates onto new insurances, and onto existing
                matches that have no rates yet. Skips creating insurances that
                already exist on the destination with the same payer ID and
                name.
              </span>
            </div>
            <div className='space-y-4 py-2'>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium'>From clinic</label>
                <ClinicSelect
                  clinics={clinics}
                  value={duplicateSourceClinicId}
                  onChange={e => setDuplicateSourceClinicId(e.target.value)}
                  placeholder='Select source clinic'
                />
              </div>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium'>To clinic</label>
                <ClinicSelect
                  clinics={rogerEmrClinics.filter(
                    c => c.id !== duplicateSourceClinicId
                  )}
                  value={duplicateDestClinicId}
                  onChange={e => setDuplicateDestClinicId(e.target.value)}
                  placeholder='Select destination clinic'
                />
              </div>
              {duplicateInsurancePreviewText && (
                <p className='text-sm text-muted-foreground'>
                  {duplicateInsurancePreviewText}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => setIsDuplicateModalOpen(false)}
                disabled={isDuplicatingInsurances}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDuplicateInsurances}
                disabled={
                  isDuplicatingInsurances ||
                  !duplicateSourceClinicId ||
                  !duplicateDestClinicId ||
                  duplicateInsurancePreview?.createdCount === 0
                }
              >
                {isDuplicatingInsurances && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                Copy Insurances
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Insurance Delete Confirmation Dialog */}
      {isRogerEmrEnabled && (
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className='sm:max-w-[400px]'>
            <DialogHeader>
              <DialogTitle>Delete Insurance</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this insurance provider? This
                action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingInsuranceId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant='destructive'
                onClick={confirmDeleteInsurance}
                disabled={isDeletingInsurance}
              >
                {isDeletingInsurance && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default InsuranceSection;
